import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Cookie } from "lucide-react";
import { getAdConsent, setAdConsent } from "../lib/analytics";

// Advertising/cookie consent banner. The Meta Pixel and CAPI advertising events do NOT
// fire until the visitor accepts (gating lives in lib/analytics). Declining still allows
// full IdeaThon participation — only optional advertising measurement is disabled.
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!getAdConsent()) setVisible(true);
  }, []);

  // Footer "Cookie Settings" re-opens the banner so users can change their choice.
  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener("ns-open-consent", open);
    return () => window.removeEventListener("ns-open-consent", open);
  }, []);

  // While visible: reserve equal space at the bottom of the page so content isn't covered,
  // and expose the banner height so the chat launcher can sit above it.
  useLayoutEffect(() => {
    if (!visible) return;
    const root = document.documentElement;
    const apply = () => {
      const h = ref.current ? ref.current.offsetHeight : 0;
      root.style.setProperty("--cookie-banner-height", `${h}px`);
      document.body.style.paddingBottom = `${h}px`;
      root.classList.add("cookie-banner-open");
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
      root.style.removeProperty("--cookie-banner-height");
      root.classList.remove("cookie-banner-open");
    };
  }, [visible]);

  if (!visible) return null;

  const choose = (granted) => {
    setAdConsent(granted);
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      data-testid="consent-banner"
      className="fixed bottom-0 inset-x-0 z-[10000] bg-[#0A0F1E]/95 backdrop-blur border-t border-white/10"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <Cookie className="w-5 h-5 text-brand shrink-0 hidden sm:block" strokeWidth={1.5} />
        <p className="text-sm text-gray-300 leading-relaxed sm:flex-1 sm:max-w-3xl" data-testid="consent-copy">
          We use cookies to analyse site traffic and improve our marketing. You can accept or decline non-essential cookies. See our <a href="/privacy" className="text-brand underline hover:text-brand/80" data-testid="consent-privacy-link">Privacy Policy</a>.
        </p>
        <div className="flex gap-3 shrink-0 w-full sm:w-auto">
          <button
            data-testid="consent-decline-btn"
            onClick={() => choose(false)}
            className="flex-1 sm:flex-none inline-flex items-center justify-center min-h-[44px] px-5 text-sm rounded-full border border-white/25 text-gray-200 hover:bg-white/5 transition-colors"
          >
            Decline
          </button>
          <button
            data-testid="consent-accept-btn"
            onClick={() => choose(true)}
            className="flex-1 sm:flex-none inline-flex items-center justify-center min-h-[44px] px-6 text-sm rounded-full bg-brand text-[#0A0F1E] font-semibold hover:opacity-90 transition-opacity"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
